import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Users, UserPlus, Search, Pencil, Trash2, Mail, Link2, Check,
  Loader2, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight,
  ChevronUp, ChevronDown, ArrowUpDown, RefreshCw,
} from 'lucide-react';
import { getAllConsultants, deleteConsultant, updateConsultant, impersonatePartner, sendConsultantInvite, getConsultantInviteLink } from '../../services/api';
import { useImpersonation } from '../../contexts/ImpersonationContext';

interface Consultant {
  id: string;
  name: string;
  email: string;
  created_at: string;
  client_count: number;
  has_agent_builder_key?: boolean;
  /** Null until the partner has been through the welcome deck. */
  onboarded_at?: string | null;
  /** Set while an accept link is still out and unused. */
  invite_expires_at?: string | null;
}

const ConsultantsList: React.FC = () => {
  const { startImpersonation } = useImpersonation();
  const [workingAs, setWorkingAs] = useState<string | null>(null);
  const [inviting, setInviting] = useState<string | null>(null);
  const [copyingLink, setCopyingLink] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState<Consultant | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  // const [provisioningId, setProvisioningId] = useState<string | null>(null);

  // Search / sort / pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 15;

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const userRole = localStorage.getItem('user_role');
    if (!token || userRole !== 'super') {
      navigate('/login');
      return;
    }
    fetchConsultants();
  }, [navigate]);

  const fetchConsultants = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllConsultants();
      setConsultants(response.consultants || response);
    } catch (error: any) {
      console.error('Error fetching consultants:', error);
      setError(error.message || 'Failed to load Solution Partners');
      toast.error(error.message || 'Failed to load Solution Partners');
    } finally {
      setLoading(false);
    }
  };

  // --- Stats ---
  const stats = useMemo(() => {
    const total = consultants.length;
    const withBuilder = consultants.filter(c => c.has_agent_builder_key).length;
    const withoutBuilder = total - withBuilder;
    const totalClients = consultants.reduce((sum, c) => sum + (c.client_count || 0), 0);
    return { total, withBuilder, withoutBuilder, totalClients };
  }, [consultants]);

  // --- Searched ---
  const searched = useMemo(() => {
    if (!searchQuery.trim()) return consultants;
    const q = searchQuery.toLowerCase();
    return consultants.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q)
    );
  }, [consultants, searchQuery]);

  // --- Sorted ---
  const sorted = useMemo(() => {
    const copy = [...searched];
    copy.sort((a, b) => {
      let va: any, vb: any;
      switch (sortField) {
        case 'name': va = a.name.toLowerCase(); vb = b.name.toLowerCase(); break;
        case 'email': va = a.email.toLowerCase(); vb = b.email.toLowerCase(); break;
        case 'client_count': va = a.client_count; vb = b.client_count; break;
        case 'created_at': va = new Date(a.created_at).getTime(); vb = new Date(b.created_at).getTime(); break;
        default: va = a.name.toLowerCase(); vb = b.name.toLowerCase();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [searched, sortField, sortDir]);

  // --- Paginated ---
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paginated = useMemo(() => {
    const start = pageIndex * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, pageIndex]);

  useEffect(() => { setPageIndex(0); }, [searchQuery]);

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <ArrowUpDown size={13} className="text-gray-300" />;
    return sortDir === 'asc'
      ? <ChevronUp size={13} className="text-brand-500" />
      : <ChevronDown size={13} className="text-brand-500" />;
  };

  // --- Handlers ---
  // Resend emails a fresh link that replaces the earlier one. Copy link mints
  // one to hand over directly and leaves the emailed link working; whichever
  // the partner uses first closes the others.
  const handleCopyInviteLink = async (consultant: Consultant) => {
    setCopyingLink(consultant.id);
    try {
      const res = await getConsultantInviteLink(consultant.id);
      try {
        await navigator.clipboard.writeText(res.url);
        setCopiedLink(consultant.id);
        window.setTimeout(() => setCopiedLink((cur) => (cur === consultant.id ? null : cur)), 2500);
        toast.success('Invite link copied. It works once, for 14 days.');
      } catch {
        toast.info(res.url);
      }
      setConsultants(prev => prev.map(c =>
        c.id === consultant.id ? { ...c, invite_expires_at: res.expires_at } : c
      ));
    } catch (err: any) {
      toast.error(err.message || 'Could not prepare the invite link');
    } finally {
      setCopyingLink(null);
    }
  };

  const handleResendInvite = async (consultant: Consultant) => {
    setInviting(consultant.id);
    try {
      const res = await sendConsultantInvite(consultant.id);
      toast.success(`Invite sent again to ${res.email}.`);
      setConsultants(prev => prev.map(c =>
        c.id === consultant.id ? { ...c, invite_expires_at: res.expires_at } : c
      ));
    } catch (err: any) {
      toast.error(err.message || 'Could not send the invite');
    } finally {
      setInviting(null);
    }
  };

  /* Connect action parked: Builder access is provisioned on the backend.
  const handleProvision = async (consultant: Consultant) => {
    setProvisioningId(consultant.id);
    try {
      await provisionConsultantAgentBuilder(consultant.id);
      toast.success(`Agent Builder connected for ${consultant.name}`);
      setConsultants(prev => prev.map(c =>
        c.id === consultant.id ? { ...c, has_agent_builder_key: true } : c
      ));
    } catch (err: any) {
      toast.error(err.message || 'Failed to connect Agent Builder');
    } finally {
      setProvisioningId(null);
    }
  };
  */

  /* Become this partner and land on their client list, which is what a super
     opening a partner is nearly always here to look at. The server picks their
     login and mints the session, so nothing here needs to know how a consultant
     is wired to a user row. A partner belongs to no company, hence the null
     client_id. */
  const handleWorkAs = async (consultant: Consultant) => {
    setWorkingAs(consultant.id);
    try {
      const data = await impersonatePartner(consultant.id);
      toast.success(`Working as ${data.user.email}`);
      startImpersonation({
        access_token: data.access_token,
        user: {
          user_id: data.user.user_id,
          client_id: null,
          name: data.user.name,
          email: data.user.email,
        },
        role: 'consultant',
        land: '/partner/clients',
        returnTo: '/super/consultants',
      });
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || 'Could not start a session as that partner');
      setWorkingAs(null);
    }
  };

  const openDeleteModal = (consultant: Consultant) => {
    setSelectedConsultant(consultant);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedConsultant(null);
  };

  const handleDeleteConsultant = async () => {
    if (!selectedConsultant) return;
    try {
      await deleteConsultant(selectedConsultant.id);
      toast.success('Solution Partner deleted');
      setConsultants(consultants.filter(c => c.id !== selectedConsultant.id));
      closeDeleteModal();
    } catch (error: any) {
      console.error('Error deleting consultant:', error);
      toast.error(error.message || 'Failed to delete the Solution Partner');
      closeDeleteModal();
    }
  };

  const openEditModal = (consultant: Consultant) => {
    setSelectedConsultant(consultant);
    setEditFormData({
      name: consultant.name,
      email: consultant.email,
      password: '',
      confirmPassword: ''
    });
    setEditErrors({});
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedConsultant(null);
    setEditFormData({ name: '', email: '', password: '', confirmPassword: '' });
    setEditErrors({});
  };

  const validateEditForm = () => {
    const errors: Record<string, string> = {};
    if (!editFormData.name.trim()) errors.name = 'Name is required';
    if (!editFormData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editFormData.email)) {
      errors.email = 'Invalid email format';
    }
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEditForm() || !selectedConsultant) return;
    setSaving(true);
    try {
      const updateData: any = { name: editFormData.name, email: editFormData.email };
      await updateConsultant(selectedConsultant.id, updateData);
      setConsultants(consultants.map(c =>
        c.id === selectedConsultant.id
          ? { ...c, name: editFormData.name, email: editFormData.email }
          : c
      ));
      toast.success('Solution Partner updated');
      closeEditModal();
    } catch (error: any) {
      console.error('Error updating consultant:', error);
      toast.error(error.message || 'Failed to update the Solution Partner');
    } finally {
      setSaving(false);
    }
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
    if (editErrors[name]) setEditErrors(prev => ({ ...prev, [name]: '' }));
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(dateString));
  };

  // ─── Render ───
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Solution Partners</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage Solution Partner accounts</p>
        </div>
        <Link
          to="/super/consultants/new"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors shadow-sm whitespace-nowrap"
        >
          <UserPlus size={16} />
          Add Solution Partner
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4">
        {[
          { label: 'Total Solution Partners', value: stats.total, icon: Users, color: 'text-brand-600 bg-brand-50' },
          /* Builder access is provisioned and self-healed on the backend now,
             so the connection tiles and the Connect action are hidden. */
          // { label: 'Agent Builder', value: stats.withBuilder, icon: CheckCircle2, color: 'text-brand-600 bg-brand-50' },
          // { label: 'Not Connected', value: stats.withoutBuilder, icon: Link2, color: 'text-amber-600 bg-amber-50' },
          { label: 'Total Clients', value: stats.totalClients, icon: Users, color: 'text-brand-500 bg-brand-50' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color}`}>
              <s.icon size={20} />
            </div>
            <div>
              <div className="text-2xl font-semibold text-gray-900">{s.value}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all"
          />
        </div>
        <span className="text-xs text-gray-400 tabular-nums">
          {searched.length} partner{searched.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
            <Loader2 size={20} className="animate-spin" />
            <span className="text-sm">Loading Solution Partners...</span>
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700 flex items-center justify-between">
              <span>{error}</span>
              <button onClick={fetchConsultants} className="flex items-center gap-1 text-red-600 hover:text-red-800 font-medium">
                <RefreshCw size={14} /> Retry
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <div className="min-w-[700px]">
                {/* Header */}
                <div className="grid grid-cols-[2fr_0.8fr_0.9fr_1.7fr] gap-4 px-5 py-3 border-b border-gray-100 bg-gray-50/60">
                  {[
                    { label: 'SOLUTION PARTNER', field: 'name' },
                    { label: 'CLIENTS', field: 'client_count' },
                    // { label: 'AGENT BUILDER', field: null },
                    { label: 'JOINED', field: 'created_at' },
                    { label: 'ACTIONS', field: null },
                  ].map(col => (
                    <div
                      key={col.label}
                      className={`text-[11px] font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1 ${col.field ? 'cursor-pointer select-none hover:text-gray-700' : ''}`}
                      onClick={() => col.field && toggleSort(col.field)}
                    >
                      {col.label}
                      {col.field && <SortIcon field={col.field} />}
                    </div>
                  ))}
                </div>

                {/* Rows */}
                {paginated.length === 0 ? (
                  <div className="px-5 py-16 text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gray-100 mb-3">
                      <Users size={22} className="text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No Solution Partners found.</p>
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')} className="text-xs text-brand-500 hover:text-brand-600 mt-2 font-medium">
                        Clear search
                      </button>
                    )}
                  </div>
                ) : (
                  paginated.map(consultant => {
                    // const isProvisioning = provisioningId === consultant.id;
                    return (
                      <div
                        key={consultant.id}
                        className="grid grid-cols-[2fr_0.8fr_0.9fr_1.7fr] gap-4 px-5 py-3.5 border-b border-gray-50 hover:bg-gray-50/70 transition-colors group items-center"
                      >
                        {/* Consultant */}
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 text-sm font-semibold shrink-0">
                            {consultant.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate group-hover:text-brand-600 transition-colors">
                              {consultant.name}
                            </div>
                            <div className="text-xs text-gray-400 truncate">{consultant.email}</div>
                            {consultant.onboarded_at === null && (
                              <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
                                <Mail size={11} />
                                {consultant.invite_expires_at ? `Invited · link open until ${formatDate(consultant.invite_expires_at)}` : 'Invited · link expired'}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Clients */}
                        <div className="text-sm text-gray-600 tabular-nums">
                          {consultant.client_count} {consultant.client_count === 1 ? 'client' : 'clients'}
                        </div>

                        {/* Agent Builder: provisioned on the backend, hidden here.
                        <div>
                          {consultant.has_agent_builder_key ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-brand-50 text-brand-600 ring-1 ring-inset ring-brand-200">
                              <CheckCircle2 size={12} />
                              Connected
                            </span>
                          ) : (
                            <button
                              onClick={() => handleProvision(consultant)}
                              disabled={isProvisioning}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-amber-50 text-amber-600 ring-1 ring-inset ring-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-50 whitespace-nowrap"
                            >
                              {isProvisioning ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
                              {isProvisioning ? 'Connecting...' : 'Connect'}
                            </button>
                          )}
                        </div>
                        */}

                        {/* Joined */}
                        <div className="text-sm text-gray-500">
                          {formatDate(consultant.created_at)}
                        </div>

                        {/* Actions. The old eye opened a read-only detail page
                            whose one unique panel was a list of this partner's
                            clients with nothing you could do to them. Becoming
                            the partner shows the same list, live, and every
                            other field on that page is already in this row. */}
                        <div className="flex items-center gap-1">
                          {/* Until the partner has set up their account, the row
                              offers the two ways to get the link to them. */}
                          {consultant.onboarded_at === null && (
                            <>
                              <button
                                onClick={() => handleCopyInviteLink(consultant)}
                                disabled={copyingLink !== null}
                                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200 hover:bg-amber-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Copy a fresh invite link to hand over directly"
                              >
                                {copyingLink === consultant.id && <Loader2 size={13} className="animate-spin" />}
                                {copyingLink !== consultant.id && copiedLink === consultant.id && <Check size={13} />}
                                {copyingLink !== consultant.id && copiedLink !== consultant.id && <Link2 size={13} />}
                                {copiedLink === consultant.id ? 'Copied' : 'Copy link'}
                              </button>
                              <button
                                onClick={() => handleResendInvite(consultant)}
                                disabled={inviting !== null}
                                className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-lg text-amber-700 ring-1 ring-inset ring-amber-200 hover:bg-amber-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Email the invite again"
                                aria-label="Email the invite again"
                              >
                                {inviting === consultant.id ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleWorkAs(consultant)}
                            disabled={workingAs !== null}
                            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 hover:text-gray-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                            title={`See the product exactly as ${consultant.name} does, for one hour`}
                          >
                            {workingAs === consultant.id && <Loader2 size={13} className="animate-spin" />}
                            Impersonate
                          </button>
                          <button
                            onClick={() => openEditModal(consultant)}
                            className="p-2 rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                            title="Edit Solution Partner"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => openDeleteModal(consultant)}
                            disabled={consultant.client_count > 0}
                            className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title={consultant.client_count > 0 ? 'Cannot delete a Solution Partner who still has clients' : 'Delete Solution Partner'}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Pagination */}
            {sorted.length > pageSize && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50/40">
                <span className="text-xs text-gray-500">
                  {pageIndex * pageSize + 1}-{Math.min((pageIndex + 1) * pageSize, sorted.length)} of {sorted.length}
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPageIndex(0)} disabled={pageIndex === 0} className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronsLeft size={16} /></button>
                  <button onClick={() => setPageIndex(i => i - 1)} disabled={pageIndex === 0} className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronLeft size={16} /></button>
                  <span className="px-3 text-xs text-gray-600 tabular-nums">Page {pageIndex + 1} of {totalPages}</span>
                  <button onClick={() => setPageIndex(i => i + 1)} disabled={pageIndex >= totalPages - 1} className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronRight size={16} /></button>
                  <button onClick={() => setPageIndex(totalPages - 1)} disabled={pageIndex >= totalPages - 1} className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronsRight size={16} /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && selectedConsultant && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Solution Partner</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete the Solution Partner <span className="font-semibold">{selectedConsultant.name}</span>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConsultant}
                className="px-4 py-2 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Consultant Modal */}
      {editModalOpen && selectedConsultant && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Solution Partner</h3>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  id="edit-name"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditInputChange}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 ${editErrors.name ? 'border-red-400' : 'border-gray-300'}`}
                  placeholder="Enter their name"
                />
                {editErrors.name && <p className="mt-1 text-xs text-red-500">{editErrors.name}</p>}
              </div>

              <div>
                <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  id="edit-email"
                  name="email"
                  value={editFormData.email}
                  onChange={handleEditInputChange}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 ${editErrors.email ? 'border-red-400' : 'border-gray-300'}`}
                  placeholder="Enter email address"
                />
                {editErrors.email && <p className="mt-1 text-xs text-red-500">{editErrors.email}</p>}
              </div>

              {/* Password change parked: a partner sets their own password on the
                  invite link and resets it themselves. The admin never types one.
              <div className="border-t border-gray-100 pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Change Password (Optional)</h4>
                <div className="space-y-3">
                  <div>
                    <label htmlFor="edit-password" className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                      <span className="ml-1 font-normal text-gray-400">signs them out everywhere</span>
                    </label>
                    <input
                      type="password"
                      id="edit-password"
                      name="password"
                      value={editFormData.password}
                      onChange={handleEditInputChange}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 ${editErrors.password ? 'border-red-400' : 'border-gray-300'}`}
                      placeholder="Leave blank to keep current password"
                    />
                    {editErrors.password && <p className="mt-1 text-xs text-red-500">{editErrors.password}</p>}
                  </div>
                  <div>
                    <label htmlFor="edit-confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                    <input
                      type="password"
                      id="edit-confirmPassword"
                      name="confirmPassword"
                      value={editFormData.confirmPassword}
                      onChange={handleEditInputChange}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 ${editErrors.confirmPassword ? 'border-red-400' : 'border-gray-300'}`}
                      placeholder="Confirm new password"
                    />
                    {editErrors.confirmPassword && <p className="mt-1 text-xs text-red-500">{editErrors.confirmPassword}</p>}
                  </div>
                </div>
              </div>

              */}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeEditModal}
                  disabled={saving}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm text-white bg-black rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultantsList;
